import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { PriceContext } from '../contexts/PriceContext';
import { UserContext } from '../contexts/UserContext';
import { useData } from '../hooks/useData';
import { useRedirectToCheckout } from '../hooks/useRedirectToCheckout';
import { useTagManager } from '../hooks/useTagManager';
import { useUserData } from '../hooks/useUserData';
import * as Utils from '../utils/general';
import { getUserLocale } from '../utils/general';
import PlanContainer from './Containers';
import Error from './Error';
import FeatureTable from './FeatureTable';
import Skeleton from './Skeleton';
import Slider from './Slider';

export default function App({ dataURL }) {
	const { data, loading, error } = useData(dataURL);
	const [isLoading, setIsLoading] = useState(true);
	const { userData, onPlanSelect, onChangePlan, onPrevBtnClick, onNextBtnClick, updatePackages } = useUserData();
	useTagManager({ userData, data });
	const isLargeScreen = useMediaQuery({ query: '(min-width: 1200px)' });
	useRedirectToCheckout(data?.general.purchase_package_version, data?.consulting_packages, userData);

	const getLabel = (key) => Utils.getLabel(data?.labels, key);

	useEffect(() => {
		if (document.startViewTransition) {
			document.startViewTransition(() => {
				flushSync(() => {
					setIsLoading(loading);
				});
			});
		} else {
			setIsLoading(loading);
		}
	}, [loading]);

	if (isLoading) {
		return (
			<>
				<Skeleton isLargeScreen={isLargeScreen} />
				<FeatureTable />
			</>
		);
	}

	if (error) {
		return <Error error={error} />;
	}

	const getContainers = () => {
		const defaultProps = {
			consultingPackages: data.consulting_packages,
			generalData: data.general,
			getLabel,
			onChangePlan,
			onNextBtnClick,
			onPlanSelect,
			onPrevBtnClick,
			updatePackages,
		};

		if (userData.step === 0) {
			const containers = Object.entries(data.plans).map(([key, plan]) => <PlanContainer {...defaultProps} id={key} key={key} plan={plan} />);
			return isLargeScreen ? containers : <Slider slides={containers} />;
		} else {
			const [key, plan] = Object.entries(data.plans).find(([key]) => userData.plan === key);
			return <PlanContainer {...defaultProps} id={key} plan={plan} />;
		}
	};

	return (
		<>
			<PriceContext.Provider value={{ useDecimalPlaces: data.general.show_exact_prices }}>
				<UserContext.Provider value={{ userData, currency: data.general.currency, locale: getUserLocale() }}>{getContainers()}</UserContext.Provider>
			</PriceContext.Provider>
			<FeatureTable features={data.features} plans={data.plans} />
		</>
	);
}

App.propTypes = {
	dataURL: PropTypes.string.isRequired,
};
